import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Table,
  Tag,
  TagLabel,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  Spinner,
  AccordionButton,
  AccordionPanel,
  Box,
} from '@chakra-ui/react';
import { DateTime } from 'luxon';
import React, { useCallback, useState } from 'react';

import {
  date as commonDate,
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  FundingRequestEntitiesFragmentGQLType,
  FundingLenderSharesFragmentGQLType,
  ResolvedLoanFragmentGQLType,
  ValleyIbsApiRecordFragmentGQLType,
  useGetParticipantValleyIbsApiRecordsForFundingRequestQuery,
  useGetNoteIncreaseValleyIbsApiRecordsForFundingRequestQuery,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { FundingRequestDrawer } from './FundingRequestDrawer';
import { usePermissionGuard } from '~/core/PermissionGuard';
import { colors } from '~/packages/common-frontend/colors';
import { Icon, TagIcon } from '~/packages/common-frontend/components/icons';
import { DEFAULT_SHORT_LOCALE_OPTIONS } from '~/packages/common-frontend/components/Timestamp';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
  truncateUuid,
} from '~/packages/common-frontend/utils';
import { Bank } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { FundingRequestStatusBadge } from '~/packages/funding-requests/ui/shared/StatusBadge';
import { DownloadAllLiveFundingNoticesButton } from '~/packages/funding-requests/ui/submit/DownloadAllLiveFundingNoticesButton';
import { useExecuteIbsApiRecord } from '~/packages/loans/hooks/useExecuteIbsApiRecord';
import { MarkFundedButtonWrapper } from '~/packages/loans/ui/components/FundTransfer';
import { DownloadLiveFundingNoticeButton } from '~/packages/loans/ui/DownloadLiveFundingNoticeButton';
import { IbsApiReExecuteConfirmModal } from '~/packages/loans/ui/ibsApi/IbsApiConfirmModal';
import { IbsApiTooltip } from '~/packages/loans/ui/ibsApi/IbsApiTooltip';

// constants
const TABLE_CELL_STYLES = {
  color: colors.gray[500],
  minWidth: '5rem',
  whiteSpace: 'normal',
};

const TABLE_HEADER_STYLES = {
  color: colors.black[95],
  fontSize: '14px',
  fontWeight: 500,
};

const FundingRequestTag = ({
  label,
  icon,
  tooltip,
}: {
  label: string;
  icon: string;
  tooltip: string;
}) => (
  <Tooltip label={tooltip} placement="top">
    <Tag variant="outline-muted" colorScheme="gray">
      <TagIcon icon={icon} />
      <TagLabel>{label}</TagLabel>
    </Tag>
  </Tooltip>
);

interface FundingRequestCardHeaderProps {
  fundingRequest: FundingRequestEntitiesFragmentGQLType;
  loan: ResolvedLoanFragmentGQLType;
  onToggle: () => void;
}
const FundingRequestCardHeader = ({
  fundingRequest,
  loan,
  onToggle,
}: FundingRequestCardHeaderProps) => {
  const isIbsIntegrationEnabled =
    usePermissionGuard(
      (context) => context.flags.productsLoansIbsIntegrationPermissions,
    ) === 'allow';

  const ibsApiRecordsQuery = useGetNoteIncreaseValleyIbsApiRecordsForFundingRequestQuery(
    { variables: { fundingRequestId: fundingRequest.fundingRequestId } },
  );
  if (ibsApiRecordsQuery.error) throw ibsApiRecordsQuery.error;

  const { executeIbsApi, isExecuteIbsApiLoading } = useExecuteIbsApiRecord();

  const ibsApiRecord = ibsApiRecordsQuery.data?.records[0] || null;

  // the link button is nested in the accordion button, so we need to prevent the accordion from toggling when clicking link
  const onToggleSidedrawer = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onToggle();
    },
    [onToggle],
  );

  return (
    <CardHeader
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        fontWeight: 500,
        gap: '1rem',
        justifyContent: 'space-between',
        w: '100%',
      }}
    >
      <Flex gap="1rem" align="center" w="50%">
        <Box w="25%" textAlign="left">
          <Typography variant="section-subtitle" trim>
            Funding Request
          </Typography>
        </Box>
        <Button
          variant="link"
          onClick={onToggleSidedrawer}
          colorScheme="blue"
          w="20%"
        >
          <Box textAlign="left" w="100%">
            <Typography
              colorScheme="blue"
              fontWeight="medium"
              variant="base-muted"
            >
              {truncateUuid(fundingRequest.fundingRequestId)}
            </Typography>
          </Box>
        </Button>
        <FundingRequestStatusBadge
          status={fundingRequest.fundingRequestStatus}
        />
      </Flex>

      <Flex gap="1rem" alignItems="center" justifyContent="flex-end">
        <FundingRequestTag
          label={commonDate.fullDateStrToHumanDateString(
            fundingRequest.originationDate,
          )}
          icon="calendar"
          tooltip="Funding Date"
        />
        <FundingRequestTag
          icon="money-bill-transfer"
          label={commonUtils.toTitleCase(fundingRequest.fundingRequestType)}
          tooltip="Funding Request Type"
        />
        <FundingRequestTag
          label={
            formatCurrencyAmount(
              fundingRequest.totalRequestedAmount,
              Bank.Currency.USD,
            ) ?? '-'
          }
          icon="circle-dollar"
          tooltip="Funded Amount"
        />
        {isIbsIntegrationEnabled &&
          fundingRequest.fundingRequestType === 'upsizing' && (
            <IbsApiTooltip
              ibsApiRecords={ibsApiRecordsQuery.data?.records || []}
              targetIbsApiRecord={ibsApiRecord}
              initialLabel="Increase Global Note Balance"
              missingLabel={!ibsApiRecord ? 'No IBS API record available' : ''}
            >
              <Button
                variant="primary-muted"
                size="md"
                colorScheme="v2-aurora"
                isLoading={!ibsApiRecordsQuery.data || isExecuteIbsApiLoading}
                isDisabled={!ibsApiRecord || ibsApiRecord.status === 'executed'}
                onClick={async () => {
                  await executeIbsApi({ ibsApiRecord, loanId: loan.id });
                }}
              >
                {(() => {
                  switch (ibsApiRecord?.status) {
                    case 'pending':
                      return 'Sync to IBS';
                    case 'executed':
                      return (
                        <Flex alignItems="center" gap="0.5rem">
                          <Icon icon="circle-check" />
                          <Typography
                            colorScheme="v2-aurora"
                            fontWeight="semibold"
                          >
                            Synced
                          </Typography>
                        </Flex>
                      );
                    case 'error':
                      return (
                        <Typography
                          variant="base-muted"
                          colorScheme="v2-aurora"
                          fontWeight="semibold"
                        >
                          Retry Sync
                        </Typography>
                      );
                    default:
                      return 'Sync to IBS';
                  }
                })()}
              </Button>
            </IbsApiTooltip>
          )}
      </Flex>
    </CardHeader>
  );
};

const FundingRequestTable = ({
  fundingRequest,
  lenderShares,
  loan,
}: {
  fundingRequest: FundingRequestEntitiesFragmentGQLType;
  lenderShares: FundingLenderSharesFragmentGQLType[];
  loan: ResolvedLoanFragmentGQLType;
}) => {
  const [
    reExecutingIbsApiRecord,
    setReExecutingIbsApiRecord,
  ] = useState<ValleyIbsApiRecordFragmentGQLType | null>(null);

  const isIbsIntegrationEnabled =
    usePermissionGuard(
      (context) => context.flags.productsLoansIbsIntegrationPermissions,
    ) === 'allow';

  const ibsApiRecordsQuery = useGetParticipantValleyIbsApiRecordsForFundingRequestQuery(
    {
      variables: {
        fundingRequestId: fundingRequest.fundingRequestId,
      },
    },
  );
  if (ibsApiRecordsQuery.error) throw ibsApiRecordsQuery.error;

  const { executeIbsApi, executingIbsApiId } = useExecuteIbsApiRecord();

  return (
    <Flex overflowX="auto" overflowY="auto" w="100%">
      <Table>
        <Thead>
          <Tr>
            <Th sx={TABLE_HEADER_STYLES}>Funded</Th>
            <Th sx={TABLE_HEADER_STYLES}>Lender</Th>
            <Th sx={TABLE_HEADER_STYLES} textAlign="right">
              Lender Share
            </Th>
            <Th sx={TABLE_HEADER_STYLES} textAlign="right">
              Lender Funding Share
            </Th>
            <Th sx={TABLE_HEADER_STYLES} textAlign="right">
              Lender All-In Rate
            </Th>
            <Th sx={TABLE_HEADER_STYLES} textAlign="right">
              Projected Interest
            </Th>
            <Th sx={TABLE_HEADER_STYLES}>Lender Funded Date</Th>

            {/* *** IBS Integration *** */}
            {isIbsIntegrationEnabled && (
              <>
                <Th sx={TABLE_HEADER_STYLES}>IBS</Th>
                <Th sx={TABLE_HEADER_STYLES}>Last IBS Sync</Th>
              </>
            )}

            {/* //TODO add menu styles here @eldarshamukhamedov @markhaghani */}
            <Th
              fontWeight={500}
              fontSize="14px"
              color="black"
              width="5%"
              textAlign="center"
            >
              <Menu>
                <MenuButton
                  fontSize="14px"
                  fontWeight="500"
                  aria-label="Download and email all funding requests"
                >
                  <HStack>
                    <span>Notice</span>
                    <Icon icon="chevron-down" />
                  </HStack>
                </MenuButton>
                <MenuList>
                  <MenuItem as="div">
                    <DownloadAllLiveFundingNoticesButton
                      loanId={loan.id}
                      creditFacilityId={loan.facility.creditFacilityId}
                      fundingRequestId={fundingRequest.fundingRequestId}
                    />
                  </MenuItem>
                </MenuList>
              </Menu>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {fundingRequest.fundingComponents.map((fundingComponent, idx) => {
            const lenderShare = lenderShares.find(
              (ls) => ls.lenderShareId === fundingComponent.lenderShareId,
            );
            finleyAssert(
              lenderShare,
              'Lender share should always exist for a loan funding components',
            );

            const ibsApiRecords =
              ibsApiRecordsQuery.data?.records.filter(
                (record) =>
                  record.externalDomainId ===
                  fundingComponent.fundingRequestComponentId,
              ) ?? [];
            const ibsApiRecord = ibsApiRecords?.[0] || null;

            const isAdminAgent =
              loan.parties.adminAgentOrganization?.id ===
              fundingComponent.lenderOrganizationId;

            const border =
              idx === fundingRequest.fundingComponents.length - 1
                ? 'none'
                : undefined;

            return (
              <Tr key={fundingComponent.lenderShareId}>
                <Td
                  sx={{
                    ...TABLE_CELL_STYLES,
                    border,
                    textAlign: 'center',
                  }}
                >
                  <MarkFundedButtonWrapper
                    loan={loan}
                    fundingRequest={fundingRequest}
                    fundingComponent={fundingComponent}
                  />
                </Td>
                <Td
                  sx={{
                    ...TABLE_CELL_STYLES,
                    fontWeight: 500,
                    border,
                    color: 'black',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {lenderShare.lenderOrganization.name}
                </Td>
                <Td
                  sx={{
                    ...TABLE_CELL_STYLES,
                    border,
                    textAlign: 'right',
                  }}
                >
                  {percentageToPercentageLabel(
                    commonUtils.divideByOrZero(
                      fundingComponent.requestedAmount,
                      fundingRequest.totalRequestedAmount,
                    ) * 100,
                    2,
                    true,
                  )}
                </Td>
                <Td
                  sx={{
                    ...TABLE_CELL_STYLES,
                    border,
                    textAlign: 'right',
                  }}
                >
                  {formatCurrencyAmount(
                    fundingComponent.requestedAmount,
                    Bank.Currency.USD,
                  )}
                </Td>
                <Td
                  sx={{
                    ...TABLE_CELL_STYLES,
                    border,
                    textAlign: 'right',
                  }}
                >
                  {/* // TODO: @markhaghani all in rate should be component level as. ex: admin agent can have different rate */}
                  {percentageToPercentageLabel(
                    loan.pricing.current.allInRate * 100,
                    5,
                    true,
                  )}
                </Td>
                <Td
                  sx={{
                    ...TABLE_CELL_STYLES,
                    border,
                    textAlign: 'right',
                  }}
                >
                  {formatCurrencyAmount(
                    fundingComponent.projectedInterest,
                    Bank.Currency.USD,
                  )}
                </Td>
                <Td sx={{ ...TABLE_CELL_STYLES, border, whiteSpace: 'nowrap' }}>
                  {fundingComponent.fundTransferDate
                    ? commonDate.fullDateStrToHumanDateString(
                        fundingComponent.fundTransferDate,
                      )
                    : '-'}
                </Td>

                {/* *** IBS Integration *** */}
                {isIbsIntegrationEnabled && (
                  <>
                    <Td
                      sx={{
                        ...TABLE_CELL_STYLES,
                        border,
                      }}
                    >
                      {isAdminAgent ? (
                        '-'
                      ) : (
                        <IbsApiTooltip
                          ibsApiRecords={ibsApiRecords}
                          targetIbsApiRecord={ibsApiRecord}
                          fundingComponent={fundingComponent}
                          initialLabel={
                            fundingRequest.fundingRequestType === 'origination'
                              ? 'Add Participant to Note with Balance'
                              : 'Increase Participant Note Balance'
                          }
                          missingLabel={
                            fundingComponent.fundTransferDate
                              ? 'No IBS API record available'
                              : 'Mark as funded before syncing to IBS'
                          }
                        >
                          <Button
                            variant="primary-muted"
                            size="md"
                            w="full"
                            colorScheme="v2-aurora"
                            isLoading={
                              !ibsApiRecordsQuery.data ||
                              (!!ibsApiRecord &&
                                executingIbsApiId === ibsApiRecord.id)
                            }
                            isDisabled={
                              !ibsApiRecord ||
                              !fundingComponent.fundTransferDate ||
                              ibsApiRecord.status === 'executed'
                            }
                            onClick={async () => {
                              if (!ibsApiRecord) return;

                              const hasPreviouslyExecuted = ibsApiRecords.some(
                                (x) => x.status === 'executed',
                              );
                              if (hasPreviouslyExecuted) {
                                // This will trigger opening re-execute modal
                                setReExecutingIbsApiRecord(ibsApiRecord);
                                return;
                              }

                              await executeIbsApi({ ibsApiRecord });
                            }}
                          >
                            {(() => {
                              switch (ibsApiRecord?.status) {
                                case 'pending':
                                  return 'Sync to IBS';
                                case 'executed':
                                  return (
                                    <Flex alignItems="center" gap="0.5rem">
                                      <Icon icon="circle-check" />
                                      <Typography
                                        colorScheme="v2-aurora"
                                        fontWeight="semibold"
                                      >
                                        Synced
                                      </Typography>
                                    </Flex>
                                  );
                                case 'error':
                                  return (
                                    <Typography
                                      variant="base-muted"
                                      colorScheme="v2-aurora"
                                      fontWeight="semibold"
                                    >
                                      Retry Sync
                                    </Typography>
                                  );
                                default:
                                  // This may be the case if no IBS API records are available yet
                                  return 'Sync to IBS';
                              }
                            })()}
                          </Button>
                        </IbsApiTooltip>
                      )}

                      {!!reExecutingIbsApiRecord && (
                        <IbsApiReExecuteConfirmModal
                          isOpen={!!reExecutingIbsApiRecord}
                          onCancel={() => setReExecutingIbsApiRecord(null)}
                          isLoading={
                            !!reExecutingIbsApiRecord &&
                            executingIbsApiId === reExecutingIbsApiRecord.id
                          }
                          onConfirm={async () => {
                            await executeIbsApi({
                              ibsApiRecord: reExecutingIbsApiRecord,
                            });
                          }}
                        />
                      )}
                    </Td>
                    <Td
                      sx={{
                        ...TABLE_CELL_STYLES,
                        whiteSpace: 'nowrap',
                        border,
                      }}
                    >
                      {(() => {
                        if (!ibsApiRecordsQuery.data) {
                          return <Spinner size="sm" />;
                        }
                        if (!ibsApiRecord || !ibsApiRecord.executedAt) {
                          return '-';
                        }

                        const executedDateTime = DateTime.fromMillis(
                          ibsApiRecord.executedAt,
                        );
                        return executedDateTime.toLocaleString(
                          DEFAULT_SHORT_LOCALE_OPTIONS,
                        );
                      })()}
                    </Td>
                  </>
                )}

                <Td
                  sx={{
                    ...TABLE_CELL_STYLES,
                    textAlign: 'center',
                    border,
                  }}
                >
                  <DownloadLiveFundingNoticeButton
                    lenderShareId={lenderShare.lenderShareId}
                    fundingRequestComponentId={
                      fundingComponent.fundingRequestComponentId
                    }
                  />
                  {/* // TODO: @markhaghani, add email funding request v2 launch */}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Flex>
  );
};

export const FundingRequestSnapshotContainer = ({
  loan,
  fundingRequest,
  lenderShares,
  loading,
}: {
  loan: ResolvedLoanFragmentGQLType;
  fundingRequest: FundingRequestEntitiesFragmentGQLType;
  lenderShares: FundingLenderSharesFragmentGQLType[];
  loading: boolean;
}) => {
  const { isOpen, onToggle } = useDisclosure();

  if (loading || !loan) {
    return <Skeleton height="200px" w="100%" />;
  }

  return (
    <>
      <Card
        size="lg"
        variant="outline"
        w="100%"
        id="funding-request-snapshot-container"
      >
        <AccordionButton>
          <FundingRequestCardHeader
            fundingRequest={fundingRequest}
            loan={loan}
            onToggle={onToggle}
          />
        </AccordionButton>

        <AccordionPanel>
          <CardBody pt="1rem">
            <FundingRequestTable
              fundingRequest={fundingRequest}
              lenderShares={lenderShares}
              loan={loan}
            />
          </CardBody>
        </AccordionPanel>
      </Card>

      <FundingRequestDrawer
        fundingRequestId={fundingRequest.fundingRequestId}
        loan={loan}
        isOpen={isOpen}
        onToggle={onToggle}
      />
    </>
  );
};
